// The `gauge-ring` structure symbol set: card 09 of mockup round 054, "GAUGE
// RING" (R1103, R1104, `ui-map-interface.md` §6.2).
//
// Every object is a ring gauge with a kind glyph inside it: a mast with two
// arcs, a truncated cone, three stepped lines, a tube with a muzzle, a crown,
// a cradle with a hull, a box with a keyhole, a pediment on three columns,
// and a mending cross. The arc runs in the holder's ink, and the rest of the
// ring is a faint track.
//
// The contested tell is a tug between two inks: the holder's arc from the
// left end, the attacker's arc from the right end, and a pale marker at the
// seam. The card trembles that seam with the clock. The atlas bakes one still
// cell, so the seam here stands at the middle of the tremble (R1104).
//
// **The gauge is the card's own 60 percent.** The wire does carry a real
// gauge for each object (`SiegeInfo.gauge10`, 0 to 50), but the site layer
// does not read it today. A live gauge would need the layer to key the atlas
// cell by the gauge as well, because one still cell holds one arc length.
// That path is not built, so the arc below is the card's fixed figure.
//
// Every radius, width, offset and alpha below is the figure card 09 draws.

import type { MarkGraphics } from '../marks';
import {
  arcPath, disc, line, PALE, ring, stroke,
  setOf, type StructureDraw, type StructureInk, type StructureKind,
  type StructureSet,
} from './shared';

/**
 * How much of the ring the gauge fills, and where that run starts and ends.
 *
 * The run is centred on the top of the ring, the way the card centres it.
 */
const SPAN = Math.PI * 2 * 0.6;
const A0 = -Math.PI / 2 - SPAN / 2;
const A1 = A0 + SPAN;

/** The ring of the Throne is wider and heavier, because it is the capital. */
const RING_R = 14;
const THRONE_RING_R = 17;
const RING_W = 3;
const THRONE_RING_W = 3.5;

/**
 * One run of the gauge ring.
 *
 * The cap is butt, so the holder's run and the attacker's run meet at the
 * seam with no overlap.
 */
function run(
  g: MarkGraphics, r: number, b0: number, b1: number,
  color: number, alpha: number, width: number,
): void {
  arcPath(g, 0, 0, r, b0, b1);
  g.stroke({ width, color, alpha, cap: 'butt' });
}

/** The glyph of one kind, inside the ring, in the holder's ink. */
function glyph(g: MarkGraphics, kind: StructureKind, col: number, al: number): void {
  const w = 1.4;
  switch (kind) {
    case 'spire':
      // The mast, with the two radio arcs over its head.
      line(g, 0, 7, 0, -6, col, al, w);
      disc(g, 0, -6, 1.5, col, al);
      arcPath(g, 0, -6, 4, -Math.PI * 0.85, -Math.PI * 0.15);
      stroke(g, col, al, w);
      arcPath(g, 0, -6, 7.5, -Math.PI * 0.8, -Math.PI * 0.2);
      stroke(g, col, al * 0.7, w);
      break;
    case 'forge':
      // The truncated cone of the chimney, with its band.
      g.poly([-7, 6, -2.5, -5, 2.5, -5, 7, 6]);
      stroke(g, col, al, w);
      line(g, -4, 2, 4, 2, col, al * 0.7, 1);
      break;
    case 'kelp':
      // Three stepped beds.
      line(g, -7, 5, 7, 5, col, al, w);
      line(g, -5, 0, 5, 0, col, al, w);
      line(g, -3, -5, 3, -5, col, al, w);
      break;
    case 'battery':
      // The tube, with the muzzle ring at its end.
      line(g, -7, 0, 4, 0, col, al, 3.2);
      ring(g, 6, 0, 2.2, col, al, w);
      break;
    case 'throne':
      // The crown, over the step of the dais.
      g.poly([-8, 5, -8, -3, -4, 0, 0, -7, 4, 0, 8, -3, 8, 5]);
      stroke(g, col, al, w);
      line(g, -8, 8, 8, 8, col, al * 0.7, 1);
      break;
    case 'drydock': {
      // The cradle, open at the top, with the hull in it.
      g.poly([-7, -4, -7, 2, -4, 6, 4, 6, 7, 2, 7, -4], false);
      stroke(g, col, al, w);
      const hull: number[] = [];
      for (let k = 0; k < 20; k++) {
        const an = (k / 20) * Math.PI * 2;
        hull.push(Math.cos(an) * 4, 1 + Math.sin(an) * 1.8);
      }
      g.poly(hull);
      stroke(g, col, al, 1);
      break;
    }
    case 'archive':
      // The box, with the keyhole in it.
      g.poly([-6, -6, 6, -6, 6, 6, -6, 6]);
      stroke(g, col, al, w);
      disc(g, 0, -1.5, 1.6, col, al);
      line(g, 0, -1, 0, 3, col, al, 1.4);
      break;
    case 'marshal':
      // The pediment, on three columns, over the floor.
      g.poly([-8, -2, 0, -7, 8, -2]);
      stroke(g, col, al, w);
      for (const x of [-5, 0, 5]) line(g, x, -1, x, 5, col, al, w);
      line(g, -8, 6, 8, 6, col, al, w);
      break;
    default:
      // The Coral Ward: the cross of the mending aura.
      line(g, -5.5, 0, 5.5, 0, col, al, 2.2);
      line(g, 0, -5.5, 0, 5.5, col, al, 2.2);
      break;
  }
}

/** One kind of the `gauge-ring` set, still. */
function markOf(kind: StructureKind): StructureDraw {
  const thr = kind === 'throne';
  const r = thr ? THRONE_RING_R : RING_R;
  const w = thr ? THRONE_RING_W : RING_W;
  return (g: MarkGraphics, ink: StructureInk): void => {
    const col = ink.own;
    const a = ink.held ? 1 : 0.65;
    // The empty track: the rest of the ring, behind the gauge.
    run(g, r, A1, A0 + Math.PI * 2, col, 0.18 * a, w);
    if (ink.foe === 0) {
      run(g, r, A0, A1, col, 0.9 * a, w);
    } else {
      // The tug: the holder from the left end, the attacker from the right,
      // and the pale marker across the seam. The card trembles the seam with
      // the clock; the atlas bakes it at the middle of the tremble (R1104).
      const mid = (A0 + A1) / 2;
      run(g, r, A0, mid, col, 0.9 * a, w);
      run(g, r, mid, A1, ink.foe, 0.95, w);
      const mx = Math.cos(mid), my = Math.sin(mid);
      line(g, mx * (r - w - 1.5), my * (r - w - 1.5),
        mx * (r + w + 1.5), my * (r + w + 1.5), PALE, 0.95, 2);
    }
    glyph(g, kind, col, 0.9 * a);
  };
}

/** Card 09, all nine kinds. */
export const GAUGE_RING_SET: StructureSet = setOf(markOf);
